<script lang="ts">
  import { goto } from '$app/navigation'
  import {
    Autocomplete,
    type AutocompleteOption,
    getToastStore,
    popup,
    type PopupSettings,
  } from '@skeletonlabs/skeleton'
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query'
  import { onMount, untrack } from 'svelte'
  import { get } from 'svelte/store'

  import { EvalFramework, evalFrameworkOptions } from './eval-frameworks'
  import {
    type ILlmEvalRun,
    type JudgeModel,
    type LaunchEvalPayload,
    LlmEvalStatus,
    type PresetType,
  } from './eval-run'
  import {
    EVALUATION_CONFIG_ERROR_MESSAGE,
    EVALUATION_DISABLED_MESSAGE,
    EvaluationEntitlementState,
  } from './evaluation-entitlement.svelte'
  import EvaluationEntitlementStatus from './EvaluationEntitlementStatus.svelte'
  import {
    DEFAULT_INSPECT_EVAL_MAX_MODEL_LEN,
    DEFAULT_INSPECT_EVAL_SAMPLE_LIMIT,
    DEFAULT_INSPECT_EVAL_TASK_IDS,
    type ExternalEvalBenchmark,
    externalEvalsCatalogQuery,
  } from './external-evals-catalog'
  import ExternalEvalBenchmarkCard from './ExternalEvalBenchmarkCard.svelte'
  import ExternalEvalTaskPicker from './ExternalEvalTaskPicker.svelte'
  import { presetLabels } from './presets'
  import Input from '@components/Input.svelte'
  import Select from '@components/Select.svelte'
  import {
    createSaveHuggingFaceTokenMutation,
    loadHuggingFaceTokens,
    saveHuggingFaceTokenIfNeeded,
  } from '@routes/huggingface-tokens/huggingface-tokens-store'
  import HuggingFaceTokenPicker from '@routes/huggingface-tokens/HuggingFaceTokenPicker.svelte'
  import { llmsStore, loadLlms } from '@routes/llm/llms/llms-store.svelte'
  import { BiasType, biasTypeLabels, type IUnlearningRun } from '@routes/llm/runs/llm-run.svelte'
  import { llmUnlearningRunsQuery, llmUnlearningRunsQueryKey } from '@routes/llm/runs/llm-runs'
  import { createClientId } from '@utils/client-id'
  import { ensureResponseOk, handleFetch } from '@utils/fetch'
  import { inferParameterCountFromIdentifier } from '@utils/model-parameter-count'
  import {
    formatParameterCountShort,
    getLlmTierUpperBound,
    getMaxSupportedLlmTier,
    Product,
  } from '@utils/product'
  import {
    type CreateMutationHelpersOptions,
    createOnError as createOnErrorHelper,
    createOnMutate as createOnMutateHelper,
    createOnSettled as createOnSettledHelper,
  } from '@utils/query'

  import Icon from '$lib/components/Icon.svelte'

  type UseSource = 'model' | 'run'
  type JudgeMode = 'default' | 'custom'

  const DEFAULT_JUDGE_PATH_OR_REPO_ID = 'google/gemma-3-12b-it'
  const DEFAULT_JUDGE_BATCH_SIZE = 16
  const DEFAULT_JUDGE_OUTPUT_TOKENS = 32
  interface Props {
    initialUseSource?: UseSource
    initialPayload?: Partial<LaunchEvalPayload>
    allowSourceSelection?: boolean
    onSuccess?: () => void
    isLaunching?: boolean
  }

  interface PreparedEvalRequest {
    url: string
    body: LaunchEvalPayload
    optimistic: ILlmEvalRun
  }

  const presetOptions = Object.entries(presetLabels) as [PresetType, string][]
  const BBQ_BIAS_TYPES = [
    BiasType.GENDER,
    BiasType.RACE,
    BiasType.NATIONALITY,
    BiasType.PHYSICAL_APPEARANCE,
    BiasType.AGE,
    BiasType.RELIGION,
  ]
  const BIAS_TYPES_BY_PRESET: Partial<Record<PresetType, BiasType[]>> = {
    BBQ_BIAS: BBQ_BIAS_TYPES,
    BBQ_UNBIAS: BBQ_BIAS_TYPES,
    UNQOVER_BIAS: [BiasType.RELIGION, BiasType.GENDER, BiasType.RACE, BiasType.NATIONALITY],
  } as const
  const toastStore = getToastStore()
  const queryClient = useQueryClient()
  const saveTokenMutation = createSaveHuggingFaceTokenMutation(queryClient)

  const defaultPayload: LaunchEvalPayload = {
    name: '',
    presetType: 'BBQ_BIAS',
  }

  const createDefaultJudgeModel = (): JudgeModel => ({
    pathOrRepoId: DEFAULT_JUDGE_PATH_OR_REPO_ID,
    token: '',
    batchSize: DEFAULT_JUDGE_BATCH_SIZE,
    outputTokens: DEFAULT_JUDGE_OUTPUT_TOKENS,
    use4bit: false,
  })

  let {
    initialUseSource = 'model',
    initialPayload = {},
    allowSourceSelection,
    onSuccess,
    isLaunching = $bindable(false),
  }: Props = $props()

  const initialUseSourceValue = untrack(() => initialUseSource)
  const initialPayloadValue = untrack(() => initialPayload)
  const sourceSelectionAllowed = untrack(
    () =>
      allowSourceSelection ??
      (initialPayloadValue.modelId === undefined && initialPayloadValue.sourceRunId === undefined),
  )

  let useSource: UseSource = $state(initialUseSourceValue)
  let judgeMode: JudgeMode = $state(initialPayloadValue.judgeModel ? 'custom' : 'default')
  let payload: LaunchEvalPayload = $state({ ...defaultPayload, ...initialPayloadValue })
  let judge: JudgeModel = $state({
    ...createDefaultJudgeModel(),
    ...(initialPayloadValue.judgeModel ?? {}),
  })
  let saveJudgeToken = $state(false)
  let judgeTokenName = $state('')
  let judgeToken = $state<string | undefined>(initialPayloadValue.judgeModel?.token)
  let judgeTokenId = $state<number | undefined>(initialPayloadValue.judgeModel?.tokenId)
  let framework = $state(initialPayloadValue.framework ?? EvalFramework.LLM_BEHAVIOR_EVAL)
  let taskIds = $state([...(initialPayloadValue.taskIds ?? DEFAULT_INSPECT_EVAL_TASK_IDS)])
  let taskPickerBenchmark = $state<ExternalEvalBenchmark | undefined>()
  // A prefilled `null` sample limit is an explicit "no limit", so it opens the field
  // empty instead of falling back to the default.
  let sampleLimit = $state<number | string>(
    initialPayloadValue.sampleLimit === undefined
      ? DEFAULT_INSPECT_EVAL_SAMPLE_LIMIT
      : (initialPayloadValue.sampleLimit ?? ''),
  )
  let maxModelLen = $state<number | string>(
    initialPayloadValue.maxModelLen === undefined
      ? DEFAULT_INSPECT_EVAL_MAX_MODEL_LEN
      : (initialPayloadValue.maxModelLen ?? ''),
  )
  const evaluationEntitlement = new EvaluationEntitlementState()
  const externalEvalsCatalog = $derived(
    createQuery({
      ...externalEvalsCatalogQuery(),
      enabled: evaluationEntitlement.loaded && evaluationEntitlement.enabled,
    }),
  )
  const showInspectEvalsFields = $derived(framework === EvalFramework.INSPECT_EVALS)
  const inspectEvalsBenchmarks = $derived($externalEvalsCatalog.data?.benchmarks ?? [])
  const inspectEvalsTaskIds = $derived(
    inspectEvalsBenchmarks.flatMap((benchmark) => benchmark.tasks.map((task) => task.id)),
  )
  const selectedInspectEvalsTaskIds = $derived(
    inspectEvalsTaskIds.filter((taskId) => taskIds.includes(taskId)),
  )
  const allInspectEvalsTasksSelected = $derived(
    inspectEvalsTaskIds.length > 0 &&
      selectedInspectEvalsTaskIds.length === inspectEvalsTaskIds.length,
  )
  const someInspectEvalsTasksSelected = $derived(
    selectedInspectEvalsTaskIds.length > 0 && !allInspectEvalsTasksSelected,
  )
  const selectedInspectEvalsBenchmarkCount = $derived(
    inspectEvalsBenchmarks.filter((benchmark) =>
      benchmark.tasks.some((task) => selectedInspectEvalsTaskIds.includes(task.id)),
    ).length,
  )
  let unlearningRuns = $state<{ id: number; name: string; runId: string }[]>([])
  let modelSearchTerm = $state('')
  let unlearningRunSearchTerm = $state('')

  const modelOptions = $derived<AutocompleteOption<string>[]>(
    Array.from($llmsStore.values()).map((llm) => ({
      value: `${llm.id}`,
      label: llm.modelName,
      keywords: [llm.modelName, llm.modelSource ?? ''].join(', '),
    })),
  )

  const unlearningRunOptions = $derived<AutocompleteOption<string>[]>(
    unlearningRuns.map((run) => ({
      value: run.runId,
      label: `${run.name} (${run.runId.slice(0, 6)})`,
      keywords: [run.name, run.runId].join(', '),
    })),
  )

  const modelAutocompletePopup: PopupSettings = {
    event: 'focus-click',
    target: 'model-autocomplete',
    placement: 'bottom-start',
  }

  const unlearningRunAutocompletePopup: PopupSettings = {
    event: 'focus-click',
    target: 'unlearning-run-autocomplete',
    placement: 'bottom-start',
  }

  const availableBiasTypes = $derived.by(() => {
    const preset = payload.presetType
    if (!preset) {
      return []
    }
    return BIAS_TYPES_BY_PRESET[preset] ?? []
  })

  $effect(() => {
    judge.token = judgeToken
    judge.tokenId = judgeTokenId
  })

  $effect(() => {
    if (showInspectEvalsFields) {
      return
    }
    if (availableBiasTypes.length === 0) {
      payload.biasType = undefined
      return
    }
    if (payload.biasType && !availableBiasTypes.includes(payload.biasType as BiasType)) {
      payload.biasType = BiasType.ALL
    }
  })

  const mutationHelpersOptions: CreateMutationHelpersOptions = {
    queryClient,
    queryKey: ['llm-eval', 'list'],
    toastStore,
  }

  const createOnMutate = $derived(
    <T,>(
      mutator: (runInfo: T, previousRuns: ILlmEvalRun[] | undefined) => ILlmEvalRun[],
      afterSetQueryData?: () => void,
    ) => createOnMutateHelper(mutator, mutationHelpersOptions, afterSetQueryData),
  )

  const createOnError = $derived((message: string) =>
    createOnErrorHelper<ILlmEvalRun[]>(message, mutationHelpersOptions),
  )

  const onSettled = $derived(createOnSettledHelper(mutationHelpersOptions))

  const parseOptionalNumber = (value: string | number | undefined): number | undefined => {
    if (value === '' || value === undefined || value === null) {
      return undefined
    }
    const parsed = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const validateJudgeModelTier = async () => {
    // TODO: Map Inspect Evals model-graded scoring to the judge-model configuration.
    if (showInspectEvalsFields || judgeMode !== 'custom') {
      return
    }
    const judgeIdentifier = judge.pathOrRepoId?.trim()
    if (!judgeIdentifier) {
      return
    }
    const activeProductEntitlements =
      evaluationEntitlement.config?.activeProductEntitlements ??
      evaluationEntitlement.config?.activeProducts ??
      []
    const maxEvalTier = getMaxSupportedLlmTier(activeProductEntitlements, Product.LLM_BEHAVIOR_EVAL)
    if (!maxEvalTier) {
      return
    }
    const tierUpperBound = getLlmTierUpperBound(maxEvalTier)
    if (!Number.isFinite(tierUpperBound)) {
      return
    }

    const judgeParameterCount = await inferParameterCountFromIdentifier(judgeIdentifier)
    if (judgeParameterCount === null || judgeParameterCount < tierUpperBound) {
      return
    }
    throw new Error(
      `Judge model size ${formatParameterCountShort(judgeParameterCount)} exceeds your ${Product.LLM_BEHAVIOR_EVAL} ${maxEvalTier} tier (supports models below ${formatParameterCountShort(tierUpperBound)}).`,
    )
  }

  const buildOptimisticRun = (requestPayload: LaunchEvalPayload): ILlmEvalRun => {
    const model = requestPayload.modelId
      ? (get(llmsStore).get(requestPayload.modelId) ?? null)
      : null
    const sourceRun = requestPayload.sourceRunId
      ? (queryClient
          .getQueryData<Map<number, IUnlearningRun>>(llmUnlearningRunsQueryKey(false))
          ?.values()
          ?.find((run) => run.runId === requestPayload.sourceRunId) ?? null)
      : null

    return {
      id: -1,
      runId: createClientId(),
      name: requestPayload.name,
      model,
      sourceRun,
      creatorId: null,
      creatorName: null,
      framework: requestPayload.framework ?? EvalFramework.LLM_BEHAVIOR_EVAL,
      presetType: requestPayload.presetType ?? null,
      taskIds: requestPayload.taskIds ?? null,
      sampleLimit: requestPayload.sampleLimit ?? null,
      maxModelLen: requestPayload.maxModelLen ?? null,
      status: LlmEvalStatus.PENDING,
      mlflowRunId: null,
      createdAt: new Date().toISOString(),
    }
  }

  const validateInspectEvalsSelection = () => {
    // The catalog is the source of truth for selectable tasks, and TanStack Query keeps
    // the previous data while a refetch is failing. Without these guards a launch either
    // blames an empty selection during the initial load or reports a stale selection.
    if ($externalEvalsCatalog.isPending) {
      throw new Error('The external evaluation catalog is still loading.')
    }
    if ($externalEvalsCatalog.isError || $externalEvalsCatalog.data === undefined) {
      throw new Error('Failed to load the external evaluation catalog. Retry before launching.')
    }
    if (selectedInspectEvalsTaskIds.length === 0) {
      throw new Error('Select at least one Inspect AI task.')
    }
  }

  const validateOptionalIntegerSetting = ({
    value,
    label,
    minimum,
  }: {
    value: string | number
    label: string
    minimum: number
  }): number | undefined => {
    const parsedValue = parseOptionalNumber(value)
    if (parsedValue === undefined) {
      return undefined
    }
    if (!Number.isInteger(parsedValue)) {
      throw new Error(`${label} must be a whole number.`)
    }
    if (parsedValue < minimum) {
      throw new Error(`${label} must be at least ${minimum}.`)
    }
    return parsedValue
  }

  const buildPreparedRequest = (): PreparedEvalRequest => {
    if (showInspectEvalsFields) {
      validateInspectEvalsSelection()
      const validatedSampleLimit = validateOptionalIntegerSetting({
        value: sampleLimit,
        label: 'Sample limit',
        minimum: 1,
      })
      const validatedMaxModelLen = validateOptionalIntegerSetting({
        value: maxModelLen,
        label: 'Maximum context length',
        minimum: 1,
      })
      const requestPayload: LaunchEvalPayload = {
        organizationId: payload.organizationId,
        name: payload.name?.trim() ?? '',
        modelId: useSource === 'model' ? payload.modelId : undefined,
        sourceRunId: useSource === 'run' ? payload.sourceRunId : undefined,
        framework: EvalFramework.INSPECT_EVALS,
        taskIds: selectedInspectEvalsTaskIds,
        sampleLimit: validatedSampleLimit ?? null,
        maxModelLen: validatedMaxModelLen ?? null,
      }
      const body = { ...requestPayload }
      delete body.framework
      return {
        url: `/external-evals/run/${useSource}`,
        body,
        optimistic: buildOptimisticRun(requestPayload),
      }
    }

    const url = `/llm-behavior-eval/run/${useSource}`
    const hasBiasOptions = availableBiasTypes.length > 0
    const requestPayload: LaunchEvalPayload = {
      ...payload,
      name: payload.name?.trim() ?? '',
      biasType: hasBiasOptions ? (payload.biasType ?? BiasType.ALL) : undefined,
    }

    if (useSource === 'model') {
      requestPayload.sourceRunId = undefined
    } else {
      requestPayload.modelId = undefined
    }

    if (!requestPayload.presetType) {
      throw new Error('Preset dataset type is required for behavior evaluations.')
    }

    if (judgeMode === 'custom') {
      const pathOrRepoId = judge.pathOrRepoId?.trim()
      if (!pathOrRepoId) {
        throw new Error('Judge repo or path is required when using a custom judge.')
      }
      const token = judge.token?.trim()
      requestPayload.judgeModel = {
        pathOrRepoId,
        token: token || undefined,
        tokenId: judge.tokenId || undefined,
        batchSize: parseOptionalNumber(judge.batchSize),
        outputTokens: parseOptionalNumber(judge.outputTokens),
        use4bit: judge.use4bit ?? undefined,
      }
    } else {
      requestPayload.judgeModel = undefined
    }

    return {
      url,
      body: requestPayload,
      optimistic: buildOptimisticRun(requestPayload),
    }
  }

  const launch = $derived(
    createMutation({
      mutationFn: async (prepared: PreparedEvalRequest) => {
        const res = await handleFetch(prepared.url, {
          method: 'POST',
          body: prepared.body,
          allowErrorResponse: true,
        })
        await ensureResponseOk(res)
        return res.json()
      },
      onSuccess: async () => {
        toastStore.trigger({
          message: 'Evaluation launched',
          background: 'variant-filled-success',
        })
        onSuccess?.()
        await goto('/llm/evals')
      },
      onMutate: createOnMutate((prepared, previousRuns = []) => {
        return [prepared.optimistic, ...previousRuns]
      }),
      onError: createOnError('Failed to launch evaluation'),
      onSettled,
    }),
  )

  $effect(() => {
    isLaunching = $launch.isPending
  })

  $effect(() => {
    if (initialPayload.sourceRunId) {
      useSource = 'run'
    }
  })

  $effect(() => {
    if (useSource === 'model') {
      payload.sourceRunId = undefined
      unlearningRunSearchTerm = ''
    } else {
      payload.modelId = undefined
      modelSearchTerm = ''
    }
  })

  $effect(() => {
    if (judgeMode === 'default') {
      judge = {}
      judgeToken = undefined
      judgeTokenId = undefined
      return
    }
    if (judgeMode === 'custom' && !judge.pathOrRepoId?.trim()) {
      judge = createDefaultJudgeModel()
      judgeToken = judge.token
      judgeTokenId = judge.tokenId
      saveJudgeToken = false
      judgeTokenName = ''
    }
  })

  onMount(async () => {
    await evaluationEntitlement.load()
    if (!evaluationEntitlement.enabled) {
      return
    }
    await loadHuggingFaceTokens().catch(() => undefined)
    if (get(llmsStore).size === 0) {
      await loadLlms()
    }
    if (payload.modelId) {
      const selectedModel = get(llmsStore).get(payload.modelId)
      if (selectedModel) {
        modelSearchTerm = selectedModel.modelName
      }
    }
    const runsMap = await queryClient.fetchQuery({
      queryKey: llmUnlearningRunsQueryKey(false),
      queryFn: llmUnlearningRunsQuery({ archived: false }),
    })
    const runsList = Array.from(runsMap.values()).map((run) => ({
      id: run.id,
      name: run.name,
      runId: run.runId,
    }))
    unlearningRuns = runsList
    if (payload.sourceRunId) {
      const selectedRun = runsList.find((run) => run.runId === payload.sourceRunId)
      if (selectedRun) {
        unlearningRunSearchTerm = `${selectedRun.name} (${selectedRun.runId.slice(0, 6)})`
      }
    }
  })

  const launchEval = async () => {
    let prepared: PreparedEvalRequest
    try {
      if (!evaluationEntitlement.loaded) {
        throw new Error('Evaluation configuration is still loading.')
      }
      if (evaluationEntitlement.loadError) {
        throw new Error(EVALUATION_CONFIG_ERROR_MESSAGE)
      }
      if (!evaluationEntitlement.enabled) {
        throw new Error(EVALUATION_DISABLED_MESSAGE)
      }
      if (!showInspectEvalsFields && judgeMode === 'custom' && saveJudgeToken) {
        const createdTokenId = await saveHuggingFaceTokenIfNeeded({
          shouldSave: true,
          token: judgeToken,
          tokenName: judgeTokenName,
          mutation: $saveTokenMutation,
        })
        if (createdTokenId === undefined) {
          throw new Error('Failed to create HuggingFace token.')
        }
        judgeTokenId = createdTokenId
        judgeToken = undefined
        saveJudgeToken = false
        judgeTokenName = ''
      }
      await validateJudgeModelTier()
      prepared = buildPreparedRequest()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to prepare eval'
      toastStore.trigger({
        message,
        background: 'variant-filled-error',
        autohide: false,
      })
      return
    }
    await $launch.mutateAsync(prepared).catch(() => undefined)
  }

  const onModelSelect = (event: CustomEvent<AutocompleteOption<string>>) => {
    const selection = event.detail
    modelSearchTerm = selection.label
    payload.modelId = selection.value ? parseInt(selection.value, 10) : undefined
  }

  const onUnlearningRunSelect = (event: CustomEvent<AutocompleteOption<string>>) => {
    const selection = event.detail
    unlearningRunSearchTerm = selection.label
    payload.sourceRunId = selection.value ?? undefined
  }

  const selectedTaskCount = (benchmark: ExternalEvalBenchmark) =>
    benchmark.tasks.filter((task) => selectedInspectEvalsTaskIds.includes(task.id)).length

  const toggleAllTasks = () => {
    taskIds = allInspectEvalsTasksSelected ? [] : [...inspectEvalsTaskIds]
  }

  const deselectBenchmark = (benchmark: ExternalEvalBenchmark) => {
    const benchmarkTaskIds = new Set(benchmark.tasks.map((task) => task.id))
    taskIds = taskIds.filter((taskId) => !benchmarkTaskIds.has(taskId))
  }

  const toggleBenchmark = (benchmark: ExternalEvalBenchmark) => {
    const benchmarkTaskIds = benchmark.tasks.map((task) => task.id)
    const allBenchmarkTasksSelected = benchmarkTaskIds.every((taskId) => taskIds.includes(taskId))
    taskIds = allBenchmarkTasksSelected
      ? taskIds.filter((taskId) => !benchmarkTaskIds.includes(taskId))
      : [...new Set([...taskIds, ...benchmarkTaskIds])]
  }

  const openTaskPicker = (benchmark: ExternalEvalBenchmark) => {
    taskPickerBenchmark = benchmark
  }

  const applyTaskPicker = (selectedTaskIds: string[]) => {
    if (!taskPickerBenchmark) {
      return
    }
    const benchmarkTaskIds = taskPickerBenchmark.tasks.map((task) => task.id)
    const otherTaskIds = taskIds.filter((taskId) => !benchmarkTaskIds.includes(taskId))
    taskIds = [...otherTaskIds, ...selectedTaskIds]
    taskPickerBenchmark = undefined
  }

  export function submit() {
    launchEval()
  }
</script>

<EvaluationEntitlementStatus entitlementState={evaluationEntitlement}>
  <form
    class="space-y-4"
    onsubmit={(e) => {
      e.preventDefault()
      submit()
    }}
  >
    {#if sourceSelectionAllowed}
      <fieldset class="space-y-2">
        <legend class="label">Evaluation source</legend>
        <div class="flex gap-6 max-md:flex-wrap">
          <label class="label"
            ><input type="radio" name="useSource" bind:group={useSource} value="model" /> Base model</label
          >
          <label class="label"
            ><input type="radio" name="useSource" bind:group={useSource} value="run" /> Unlearned model</label
          >
        </div>
      </fieldset>

      {#if useSource === 'model'}
        <div class="space-y-2">
          <label class="label" for="model-search">Base model</label>
          <div>
            <input
              id="model-search"
              class="input my-0 box-border flex w-fit min-w-96 px-2 py-1 dark:text-white max-md:w-full max-md:min-w-0 max-md:max-w-96"
              type="search"
              placeholder="Search models"
              autocomplete="off"
              bind:value={modelSearchTerm}
              oninput={() => {
                payload.modelId = undefined
              }}
              use:popup={modelAutocompletePopup}
            />
            <div
              class="card w-modal mt-2 max-h-48 max-w-[50vw] overflow-y-auto p-2 max-md:w-[calc(100vw-6rem)] max-md:max-w-none"
              data-popup="model-autocomplete"
              tabindex="-1"
            >
              {#if modelOptions.length === 0}
                <p class="text-sm text-surface-300">No models available.</p>
              {:else}
                <Autocomplete
                  bind:input={modelSearchTerm}
                  options={modelOptions}
                  on:selection={onModelSelect}
                />
              {/if}
            </div>
          </div>
        </div>
      {:else}
        <div class="space-y-2">
          <label class="label" for="unlearning-run-search">Unlearned model</label>
          <div>
            <input
              id="unlearning-run-search"
              class="input my-0 box-border flex w-fit min-w-96 px-2 py-1 dark:text-white max-md:w-full max-md:min-w-0 max-md:max-w-96"
              type="search"
              placeholder="Search unlearning runs"
              autocomplete="off"
              bind:value={unlearningRunSearchTerm}
              oninput={() => {
                payload.sourceRunId = undefined
              }}
              use:popup={unlearningRunAutocompletePopup}
            />
            <div
              class="card w-modal mt-2 max-h-48 max-w-[50vw] overflow-y-auto p-2 max-md:w-[calc(100vw-6rem)] max-md:max-w-none"
              data-popup="unlearning-run-autocomplete"
              tabindex="-1"
            >
              {#if unlearningRunOptions.length === 0}
                <p class="text-sm text-surface-300">No unlearning runs available.</p>
              {:else}
                <Autocomplete
                  bind:input={unlearningRunSearchTerm}
                  options={unlearningRunOptions}
                  on:selection={onUnlearningRunSelect}
                />
              {/if}
            </div>
          </div>
        </div>
      {/if}
    {/if}

    <div class="space-y-2">
      <label class="label" for="name"
        >Name <span class="text-surface-500 dark:text-surface-300">(optional)</span></label
      >
      <Input
        id="name"
        bind:value={payload.name}
        placeholder="e.g. July safety benchmark"
        class="max-md:w-full max-md:max-w-96"
        minWidth="min-w-96 max-md:min-w-0"
      />
    </div>

    <div role="separator" class="h-px bg-surface-400"></div>

    <fieldset class="space-y-2">
      <legend class="label">Evaluation type</legend>
      <div class="grid gap-2 sm:grid-cols-2">
        {#each evalFrameworkOptions as option (option.value)}
          <label
            class="relative flex min-h-20 gap-3 rounded-lg border-2 p-3 transition-colors {framework ===
            option.value
              ? 'cursor-pointer border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
              : 'cursor-pointer border-surface-500 bg-surface-50 hover:border-primary-500 dark:border-surface-600 dark:bg-surface-800'}"
          >
            <input
              type="radio"
              name="framework"
              bind:group={framework}
              value={option.value}
              aria-label={option.label}
              class="m-0 mt-0.5 h-4 w-4 shrink-0"
            />
            <span class="min-w-0 [overflow-wrap:anywhere]">
              <span class="flex items-center gap-2 font-medium">
                {#if option.value === EvalFramework.LLM_BEHAVIOR_EVAL}
                  <Icon icon="iconoir:shield-check" class="h-4 w-4 text-primary-500" />
                {/if}
                {option.label}
              </span>
              <span class="mt-1 block text-surface-500 dark:text-surface-300">
                {option.description}
              </span>
            </span>
          </label>
        {/each}
      </div>
    </fieldset>

    {#if showInspectEvalsFields}
      <section class="space-y-4" aria-labelledby="inspect-ai-evaluation-heading">
        <fieldset class="min-w-0 space-y-3">
          <legend class="font-medium">Inspect AI settings</legend>
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="space-y-2">
              <label class="label" for="sampleLimit">Sample limit per task</label>
              <Input
                id="sampleLimit"
                minWidth="min-w-0"
                type="number"
                bind:value={sampleLimit}
                min={1}
                placeholder="No limit"
                aria-describedby="sample-limit-help"
              />
              <p id="sample-limit-help" class="text-xs text-surface-500 dark:text-surface-300">
                Limits each selected task to the same positive number of samples. Leave empty to
                evaluate every sample of every selected task, which takes much longer.
              </p>
            </div>

            <div class="space-y-2">
              <label class="label" for="maxModelLen">Maximum context length</label>
              <Input
                id="maxModelLen"
                minWidth="min-w-0"
                type="number"
                bind:value={maxModelLen}
                min={1}
                aria-describedby="max-model-len-help"
              />
              <p id="max-model-len-help" class="text-xs text-surface-500 dark:text-surface-300">
                Caps the combined prompt and generated tokens for each model request, up to the
                model's supported context length. Leave empty to use the context length from the
                model's configuration.
              </p>
            </div>
          </div>
        </fieldset>

        <fieldset class="min-w-0 space-y-3">
          <legend id="inspect-ai-evaluation-heading" class="font-medium">
            Inspect AI benchmarks
          </legend>
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="space-y-1 text-xs text-surface-500 dark:text-surface-300">
              <p>Select benchmarks, then customize their tasks when needed.</p>
              {#if inspectEvalsTaskIds.length > 0}
                <p aria-live="polite">
                  {selectedInspectEvalsBenchmarkCount} of {inspectEvalsBenchmarks.length}
                  benchmarks · {selectedInspectEvalsTaskIds.length} of {inspectEvalsTaskIds.length}
                  tasks selected
                </p>
              {/if}
            </div>
            <label class="label ml-auto flex w-fit cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={allInspectEvalsTasksSelected}
                indeterminate={someInspectEvalsTasksSelected}
                aria-label="Select all benchmark tasks"
                onchange={toggleAllTasks}
                disabled={$externalEvalsCatalog.isPending || inspectEvalsTaskIds.length === 0}
              />
              Select all
            </label>
          </div>
          {#if $externalEvalsCatalog.isPending}
            <p role="status" class="text-sm text-surface-500 dark:text-surface-300">
              Loading external evaluation catalog...
            </p>
          {:else if $externalEvalsCatalog.isError}
            <div role="alert" class="space-y-2 text-sm">
              <p>Failed to load the external evaluation catalog.</p>
              <button
                type="button"
                class="variant-ghost-surface btn"
                onclick={() => $externalEvalsCatalog.refetch()}>Retry</button
              >
            </div>
          {:else if inspectEvalsBenchmarks.length === 0}
            <p class="text-sm text-surface-500 dark:text-surface-300">
              No external evaluations are currently enabled.
            </p>
          {:else}
            <div class="space-y-2" role="group" aria-labelledby="selected-benchmarks-label">
              <p id="selected-benchmarks-label" class="text-sm font-medium">Selected benchmarks</p>
              {#if selectedInspectEvalsBenchmarkCount === 0}
                <p class="text-xs text-surface-500 dark:text-surface-300">
                  No benchmarks selected. Select benchmarks from the grid below.
                </p>
              {:else}
                <ul class="flex flex-wrap gap-2">
                  {#each inspectEvalsBenchmarks as benchmark (benchmark.id)}
                    {@const benchmarkSelectedTaskCount = selectedTaskCount(benchmark)}
                    {#if benchmarkSelectedTaskCount > 0}
                      <li
                        class="flex max-w-full items-center gap-2 rounded-lg bg-surface-200 pl-3 pr-1 text-sm dark:bg-surface-700"
                      >
                        <span class="min-w-0 [overflow-wrap:anywhere]">
                          {benchmark.title}
                          <span class="text-surface-600 dark:text-surface-300">
                            · {#if benchmarkSelectedTaskCount === benchmark.tasks.length}
                              {benchmarkSelectedTaskCount}
                              {benchmarkSelectedTaskCount === 1 ? 'task' : 'tasks'}
                            {:else}
                              {benchmarkSelectedTaskCount}/{benchmark.tasks.length} tasks
                            {/if}
                          </span>
                        </span>
                        <button
                          type="button"
                          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-surface-600 hover:bg-surface-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-surface-300 dark:hover:bg-surface-600"
                          aria-label={`Deselect ${benchmark.title}`}
                          onclick={() => deselectBenchmark(benchmark)}
                        >
                          <Icon icon="iconoir:xmark" width="14" height="14" />
                        </button>
                      </li>
                    {/if}
                  {/each}
                </ul>
              {/if}
            </div>
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {#each inspectEvalsBenchmarks as benchmark (benchmark.id)}
                {@const benchmarkSelectedTaskCount = selectedTaskCount(benchmark)}
                <ExternalEvalBenchmarkCard
                  {benchmark}
                  selectedTaskCount={benchmarkSelectedTaskCount}
                  onToggle={() => toggleBenchmark(benchmark)}
                  onChooseTasks={() => openTaskPicker(benchmark)}
                />
              {/each}
            </div>
          {/if}
        </fieldset>
      </section>
    {:else}
      <section class="space-y-4" aria-labelledby="llm-behavior-evaluation-heading">
        <h3 id="llm-behavior-evaluation-heading" class="font-medium">LLM behavior evaluation</h3>

        <div class="space-y-2">
          <label class="label" for="presetType">Preset dataset</label>
          <Select
            id="presetType"
            bind:value={payload.presetType}
            required
            class="my-0 w-full px-4 py-3"
          >
            {#each presetOptions as [value, label] (value)}
              <option {value}>{label}</option>
            {/each}
          </Select>
        </div>

        {#if availableBiasTypes.length > 0}
          <div class="space-y-2">
            <label class="label" for="biasType"
              >Bias type <span class="text-surface-500 dark:text-surface-300">(optional)</span
              ></label
            >
            <Select id="biasType" bind:value={payload.biasType} class="my-0 w-full px-4 py-3">
              <option value={BiasType.ALL}>All</option>
              {#each availableBiasTypes as biasType (biasType)}
                <option value={biasType}>{biasTypeLabels[biasType]}</option>
              {/each}
            </Select>
          </div>
        {/if}

        <fieldset class="space-y-3">
          <legend class="label">Judge</legend>
          <div class="flex gap-6">
            <label class="label"
              ><input type="radio" name="judgeMode" bind:group={judgeMode} value="default" /> Default
              judge</label
            >
            <label class="label"
              ><input type="radio" name="judgeMode" bind:group={judgeMode} value="custom" /> Custom judge</label
            >
          </div>

          {#if judgeMode === 'custom' && typeof judge.pathOrRepoId === 'string'}
            <div class="grid gap-3">
              <div class="space-y-2">
                <label class="label" for="judgeModel">Judge repo or path</label>
                <Input id="judgeModel" bind:value={judge.pathOrRepoId} required />
              </div>
              <HuggingFaceTokenPicker
                bind:token={judgeToken}
                bind:tokenId={judgeTokenId}
                bind:saveToken={saveJudgeToken}
                bind:tokenName={judgeTokenName}
                customLabel="HuggingFace token (optional)"
              />
              <div class="space-y-2">
                <label class="label" for="judgeBatchSize">Batch size</label>
                <Input id="judgeBatchSize" type="number" bind:value={judge.batchSize} min={1} />
              </div>
              <div class="space-y-2">
                <label class="label" for="judgeOutputTokens">Output tokens</label>
                <Input
                  id="judgeOutputTokens"
                  type="number"
                  bind:value={judge.outputTokens}
                  min={1}
                />
              </div>
              <label class="label flex items-center gap-2">
                <input type="checkbox" bind:checked={judge.use4bit} /> Use 4-bit
              </label>
            </div>
          {/if}
        </fieldset>
      </section>
    {/if}
  </form>
</EvaluationEntitlementStatus>

{#if taskPickerBenchmark}
  <ExternalEvalTaskPicker
    benchmark={taskPickerBenchmark}
    selectedTaskIds={selectedInspectEvalsTaskIds}
    onApply={applyTaskPicker}
    onClose={() => {
      taskPickerBenchmark = undefined
    }}
  />
{/if}
